/**
 * The Running order's teaching view: one activity at a time, in order, from
 * the same plan controller the outline edits. It changes nothing by itself:
 * no timer, no auto-advance, no completion marks, no attendance and no
 * broadcast. Its place is the session address's `teach=<activity>`, so Back,
 * Return and reload keep the activity; entering and leaving never touch the
 * plan's draft, notes or pending save.
 */
import { useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { draftActivities, draftMinutes, type DraftActivity } from "@/components/courses/unit-planner";
import { ACTIVITY_POINTER_BOX, activityPointerRecord } from "@/components/assignments/coursework/teaching-draft-model";
import { useTabDraft, useTabDrafts } from "@/components/ui/box-draft";
import type { VisitAddress } from "@/components/ui/box.schema";
import { BentoVisit } from "@/components/ui/box-visit-context";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api/client";
import type { ActivityCoursework, ActivityReference, CourseUnit } from "@/lib/api/courseUnits";
import { teachingDraftsApi } from "@/lib/api/teachingDrafts";
import { formatInCourseZone } from "@/lib/session-portal";

import { activityNumber, activityWork } from "./boxes/class-overview-model";
import { OutlineOpenRequest } from "./outline-open-request";
import { TEACH_COPY } from "./teaching-view-copy";


type Memory = { activity: string; scrollY: number | null };

function useTeachAddress() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const write = (change: (next: URLSearchParams) => void, replace: boolean) => {
    const next = new URLSearchParams(location.search);
    change(next);
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : "", hash: location.hash }, { replace, state: location.state });
  };
  return { teach: params.get("teach"), write };
}

/** The Running order's body: its outline, or the teaching view over the same plan. */
export function RunningOrderBody({ sessionId, courseId, timezone, unit, activities: drafted, editable, createAllowed, outline, saveStatus, resourceAddress }: {
  sessionId: string; courseId: string; timezone: string;
  /** The acknowledged plan: what is saved, with its references and coursework links. */
  unit: CourseUnit;
  /** The plan as written now, from its draft owner; absent when read-only. */
  activities?: DraftActivity[];
  editable: boolean;
  createAllowed: boolean;
  outline: ReactNode;
  saveStatus?: ReactNode;
  /** A referenced reading or material's own address, by its placement. */
  resourceAddress?: (placementId: string) => VisitAddress;
}) {
  const activities = drafted ?? draftActivities(unit.activities);
  const { teach, write } = useTeachAddress();
  const memory = useTabDraft<Memory>({ box: "teaching-view", record: sessionId, version: null, initial: { activity: "", scrollY: null } });
  const openerId = `teach-${sessionId}-enter`;
  const region = useRef<HTMLDivElement>(null);
  const was = useRef(teach);
  const [change, setChange] = useState<{ id: string; token: number } | null>(null);
  const changing = useRef(false);
  // Enter scrolls the working region into view; leaving, by its control or by
  // Back, restores the scroll position and the control that opened it.
  useEffect(() => {
    const before = was.current;
    was.current = teach;
    if (teach !== null && before === null) region.current?.scrollIntoView?.({ block: "start" });
    if (teach === null && before !== null) {
      // Change this activity hands focus to the outline's row instead.
      if (changing.current) { changing.current = false; return; }
      const y = memory.value.scrollY;
      requestAnimationFrame(() => {
        if (y !== null) window.scrollTo({ top: y });
        document.getElementById(openerId)?.focus({ preventScroll: true });
      });
    }
  }, [teach, memory.value.scrollY, openerId]);
  const enter = () => {
    const retained = activities.find(activity => activity.id === memory.value.activity)?.id ?? activities[0]?.id ?? "";
    memory.set({ activity: retained, scrollY: window.scrollY });
    write(next => next.set("teach", retained || "none"), false);
  };
  const leave = () => write(next => next.delete("teach"), true);
  const select = (activityId: string) => {
    memory.set(current => ({ ...current, activity: activityId }));
    write(next => next.set("teach", activityId), true);
  };
  const teaching = teach !== null;
  return (
    <div ref={region} className="grid gap-3" data-running-order-body>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {teaching
          ? <Button type="button" variant="subtle" size="sm" onClick={leave}>{TEACH_COPY.leave}</Button>
          : <Button id={openerId} type="button" variant="subtle" size="sm" disabled={!activities.length} onClick={enter} data-teach-enter>{TEACH_COPY.enter}</Button>}
      </div>
      {teaching && <TeachingView courseId={courseId} timezone={timezone} unit={unit} activities={activities} teach={teach} editable={editable} createAllowed={createAllowed}
        resourceAddress={resourceAddress} onSelect={select}
        onChange={editable ? activityId => { changing.current = true; leave(); setChange({ id: activityId, token: Date.now() }); } : undefined} />}
      {/* Kept mounted while teaching: its open rows and pending Undo survive. */}
      <div hidden={teaching} className="grid gap-2"><OutlineOpenRequest.Provider value={change}>{outline}</OutlineOpenRequest.Provider>{saveStatus}</div>
    </div>
  );
}

function TeachingView({ courseId, timezone, unit, activities, teach, editable, createAllowed, resourceAddress, onSelect, onChange }: {
  courseId: string; timezone: string; unit: CourseUnit; activities: DraftActivity[]; teach: string;
  editable: boolean; createAllowed: boolean; resourceAddress?: (placementId: string) => VisitAddress;
  onSelect: (activityId: string) => void; onChange?: (activityId: string) => void;
}) {
  const host = useContext(BentoVisit);
  const selectId = useId();
  const index = activities.findIndex(activity => activity.id === teach);
  if (!activities.length) return <p className="text-ui text-muted-foreground">{TEACH_COPY.none}</p>;
  if (index < 0) return (
    <div role="status" className="grid justify-items-start gap-2" data-teach-removed>
      <p className="text-ui">{TEACH_COPY.removed}</p>
      {/* The other choice is the view's own Leave control, above. */}
      <Button type="button" variant="outline" size="sm" onClick={() => onSelect(activities[0].id)}>{TEACH_COPY.first}</Button>
    </div>
  );
  const activity = activities[index];
  const minutes = draftMinutes(activity);
  const saved = unit.activities?.some(item => item.id === activity.id) ?? false;
  const visit = (address: VisitAddress) => host?.visit(address, { box: "running-order", row: activity.id });
  return (
    <section aria-labelledby={`${selectId}-title`} className="grid gap-4 sm:grid-cols-[minmax(11rem,15rem)_minmax(0,1fr)] sm:gap-6" data-teaching-view={activity.id}>
      <div className="grid content-start gap-2">
        <div className="grid gap-1.5 sm:hidden">
          <Label htmlFor={selectId}>{TEACH_COPY.select}</Label>
          <select id={selectId} value={activity.id} onChange={event => onSelect(event.target.value)}
            className="h-[var(--control-height)] rounded-control border border-input bg-background px-2 text-ui">
            {activities.map((item, at) => <option key={item.id} value={item.id}>{`${activityNumber(at)} ${item.label.trim() || "Untitled activity"}`}</option>)}
          </select>
        </div>
        <ol aria-label={TEACH_COPY.list} className="hidden gap-0.5 sm:grid">
          {activities.map((item, at) => (
            <li key={item.id}>
              <button type="button" aria-current={at === index ? "step" : undefined} onClick={() => onSelect(item.id)}
                className={`grid w-full grid-cols-[1.75rem_minmax(0,1fr)] gap-x-2 rounded-control px-2 py-1.5 text-start text-ui outline-none hover:bg-well focus-visible:ring-2 focus-visible:ring-ring ${at === index ? "bg-well font-semibold" : ""}`}>
                <span className="font-data text-meta tabular-nums text-muted-foreground">{activityNumber(at)}</span>
                <span className="[overflow-wrap:anywhere]" dir="auto">{item.label.trim() || "Untitled activity"}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>
      <div className="grid content-start gap-4">
        <div className="grid gap-1">
          <p aria-live="polite" className="font-data text-meta tabular-nums text-muted-foreground">{TEACH_COPY.position(index + 1, activities.length)}{minutes !== null ? ` · ${minutes} min` : ""}</p>
          <h4 id={`${selectId}-title`} dir="auto" className="text-section font-semibold text-foreground [overflow-wrap:anywhere]">{activity.label.trim() || "Untitled activity"}</h4>
        </div>
        <div className="grid gap-1">
          {activity.detail.trim()
            ? <p dir="auto" className="whitespace-pre-wrap text-body [overflow-wrap:anywhere]">{activity.detail.trim()}</p>
            : <p className="text-ui text-muted-foreground">{TEACH_COPY.noPrompt}</p>}
          <p className="text-meta text-muted-foreground">{TEACH_COPY.private}</p>
        </div>
        <ActivityReferenceLine reference={unit.activity_references?.[activity.id]} courseId={courseId} resourceAddress={resourceAddress} onVisit={visit} />
        <ActivityCourseworkPart courseId={courseId} unitId={unit.id} timezone={timezone} activityId={activity.id} links={unit.activity_coursework} reference={unit.activity_references?.[activity.id]}
          saved={saved} createAllowed={createAllowed} onVisit={visit} />
        <div className="flex flex-wrap items-center gap-2 border-t border-rule pt-3">
          <Button type="button" variant="outline" size="sm" disabled={index === 0} onClick={() => onSelect(activities[index - 1].id)}><ChevronLeft aria-hidden /> {TEACH_COPY.previous}</Button>
          <Button type="button" variant="outline" size="sm" disabled={index === activities.length - 1} onClick={() => onSelect(activities[index + 1].id)}>{TEACH_COPY.next} <ChevronRight aria-hidden /></Button>
          {editable && onChange && <Button type="button" variant="link" size="sm" className="ms-auto px-0" onClick={() => onChange(activity.id)}>{TEACH_COPY.change}</Button>}
        </div>
      </div>
    </section>
  );
}

function ActivityReferenceLine({ reference, courseId, resourceAddress, onVisit }: { reference: ActivityReference | undefined; courseId: string; resourceAddress?: (placementId: string) => VisitAddress; onVisit: (address: VisitAddress) => void }) {
  if (!reference) return null;
  if (reference.status === "unavailable") return <p className="text-meta text-muted-foreground">{TEACH_COPY.unavailable}</p>;
  const address: VisitAddress = reference.kind === "assignment" ? `work:${reference.id}` : resourceAddress?.(reference.id) ?? `material:${courseId}/${reference.id}`;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="text-ui"><span className="text-muted-foreground">{TEACH_COPY.reference[reference.kind]}: </span><span dir="auto">{reference.title}</span></span>
      <Button type="button" variant="link" size="sm" className="px-0" data-visit-to={address} onClick={() => onVisit(address)}>{TEACH_COPY.open[reference.kind]}</Button>
    </div>
  );
}

function ActivityCourseworkPart({ courseId, unitId, timezone, activityId, links, reference, saved, createAllowed, onVisit }: {
  courseId: string; unitId: string; timezone: string; activityId: string; links: Record<string, ActivityCoursework[]> | undefined; reference: ActivityReference | undefined;
  saved: boolean; createAllowed: boolean; onVisit: (address: VisitAddress) => void;
}) {
  const work = activityWork(courseId, activityId, links, reference);
  const pointers = useTabDrafts<{ id: string }>(ACTIVITY_POINTER_BOX);
  // Create another sets this activity's draft pointer aside, so the author starts a new
  // private draft; the earlier ones stay listed here. Create keeps the pointer, so an
  // unsaved draft begun from this activity returns.
  const create = (another: boolean) => {
    if (another) pointers({ record: activityPointerRecord(courseId, unitId, activityId), version: null, initial: { id: "" } }).end();
    onVisit(work.create);
  };
  const when = (value: string | null) => value ? formatInCourseZone(value, timezone, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;
  const any = work.drafts.length > 0 || work.published.length > 0;
  if (!any && !createAllowed) return null;
  return (
    <div className="grid gap-2" data-activity-coursework={activityId}>
      <h5 className="text-meta font-medium text-muted-foreground">{TEACH_COPY.coursework}</h5>
      {work.published.length > 0 && <ul className="grid gap-2">{work.published.map(link => (
        <li key={link.id} className="grid gap-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span dir="auto" className="text-ui font-medium [overflow-wrap:anywhere]">{link.assignment!.title}</span>
            <span className="text-meta text-muted-foreground">{TEACH_COPY.status[link.assignment!.status]}</span>
            <Button type="button" variant="link" size="sm" className="px-0" onClick={() => onVisit(`work:${link.assignment!.id}`)}>{TEACH_COPY.open.assignment}</Button>
          </div>
          {createAllowed && <UnlinkControl courseId={courseId} link={link} />}
        </li>
      ))}</ul>}
      {work.drafts.length === 1 && (() => {
        const link = work.drafts[0];
        return <div className="grid gap-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span dir="auto" className="text-ui font-medium [overflow-wrap:anywhere]">{link.draft!.title || "Untitled coursework"}</span>
            <span className="text-meta text-muted-foreground">Private draft{link.draft!.saved_at ? ` · ${TEACH_COPY.savedBy(when(link.draft!.saved_at)!)}` : ""}</span>
            <Button type="button" variant="outline" size="sm" onClick={() => onVisit(`teaching-draft:${courseId}/${link.draft!.id}`)}>{TEACH_COPY.continue}</Button>
          </div>
          {createAllowed && <UnlinkControl courseId={courseId} link={link} />}
        </div>;
      })()}
      {work.drafts.length > 1 && <fieldset className="grid gap-1.5">
        <legend className="text-ui">{TEACH_COPY.choose}</legend>
        <ul className="grid gap-1.5">{work.drafts.map(link => (
          <li key={link.id} className="grid gap-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Button type="button" variant="link" size="sm" className="h-auto px-0 text-start" onClick={() => onVisit(`teaching-draft:${courseId}/${link.draft!.id}`)}>
                <span dir="auto" className="[overflow-wrap:anywhere]">{link.draft!.title || "Untitled coursework"}</span>
              </Button>
              <span className="text-meta text-muted-foreground">{link.draft!.saved_at ? TEACH_COPY.savedBy(when(link.draft!.saved_at)!) : "Private draft"}</span>
            </div>
            {createAllowed && <UnlinkControl courseId={courseId} link={link} />}
          </li>
        ))}</ul>
      </fieldset>}
      {createAllowed && (saved
        ? <div><Button type="button" variant={any ? "subtle" : "outline"} size="sm" data-visit-to={work.create} onClick={() => create(any)}>{any ? TEACH_COPY.another : TEACH_COPY.create}</Button></div>
        : <p className="text-meta text-muted-foreground">{TEACH_COPY.unsaved}</p>)}
    </div>
  );
}

type Removal = { key: string; state: "sending" | "failed" | "unknown"; message?: string };

/** Remove from activity: retires only the link, keyed so an unanswered request is checked, never repeated as new. */
function UnlinkControl({ courseId, link }: { courseId: string; link: ActivityCoursework }) {
  const client = useQueryClient();
  const [removal, setRemoval] = useState<Removal | null>(null);
  const send = async (key: string) => {
    setRemoval({ key, state: "sending" });
    try {
      await teachingDraftsApi.unlinkActivity(courseId, link.id, link.version, key);
      setRemoval(null);
      await client.invalidateQueries({ queryKey: ["course-units", courseId] });
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408) {
        setRemoval({ key, state: "failed", message: error.message });
        if (error.status === 409) void client.invalidateQueries({ queryKey: ["course-units", courseId] });
      } else setRemoval({ key, state: "unknown" });
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-muted-foreground">
      {removal?.state === "unknown"
        ? <><span role="status">{TEACH_COPY.removeUnknown}</span><Button type="button" variant="link" size="sm" className="h-auto px-0" onClick={() => void send(removal.key)}>{TEACH_COPY.check}</Button></>
        : <Button type="button" variant="link" size="sm" className="h-auto px-0 text-muted-foreground" disabled={removal?.state === "sending"} title={TEACH_COPY.removeNote}
            onClick={() => void send(crypto.randomUUID())}>{removal?.state === "sending" ? TEACH_COPY.removing : TEACH_COPY.remove}</Button>}
      {removal?.state === "failed" && <span role="status">{TEACH_COPY.removeFailed} {removal.message}</span>}
    </div>
  );
}
